import {
  makeObservable,
  computed,
  observable,
  action,
  runInAction,
  onBecomeObserved,
  onBecomeUnobserved,
} from "mobx";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { SpaceAccountCalendarConnectionModelData } from "@/store/calendar/space-account-calendar-connection/types";
import { SyncUpdateValue } from "@/store/sync/types";
import { liveQuery, Subscription } from "dexie";
import { Uuid } from "@/domains/global/identifiers";
import { SpaceAccountCalendarObservable } from "@/store/calendar/space-account-calendar/SpaceAccountCalendarObservable";

export class SpaceAccountCalendarConnectionObservable extends BaseSyncModel<SpaceAccountCalendarConnectionModelData> {
  public modelKind = SyncModelKind.SpaceAccountCalendarConnection;

  liveQuerySubscription?: Subscription;
  subscribedCalendars: Uuid[] = [];

  constructor({
    id,
    data,
    store,
  }: {
    id: string;
    data: SyncUpdateValue<SpaceAccountCalendarConnectionModelData>;
  } & WithAppStore) {
    super({ id, data, store });

    makeObservable<this, "store">(this, {
      modelKind: false,
      liveQuerySubscription: observable,
      subscribedCalendars: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
      associatedSpaceAccountId: computed,
      associatedEmailAddress: computed,
      providerKind: computed,
      isConnected: computed,
      allCalendars: computed,
      store: false,
    });

    onBecomeObserved(this, "subscribedCalendars", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "subscribedCalendars", () => this.unsubscribeLiveQuery());
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(async () => {
      const calendars = await this.store.spaceAccountCalendars.localTable
        .filter(
          calendar =>
            calendar.model_data?.associated_space_account_calendar_connection_id === this.id
        )
        .toArray();
      return calendars.map(calendar => calendar.model_id) as Uuid[];
    }).subscribe({
      next: calendarIds => {
        runInAction(() => {
          this.subscribedCalendars = calendarIds as unknown as Uuid[];
        });
      },
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get allCalendars(): SpaceAccountCalendarObservable[] {
    return this.store.spaceAccountCalendars
      .getMany(this.subscribedCalendars)
      .filter(calendar => !!calendar) as SpaceAccountCalendarObservable[];
  }

  get associatedSpaceAccountId() {
    return this.modelData.associated_space_account_id;
  }

  get associatedEmailAddress() {
    return this.modelData.associated_email_address;
  }

  get providerKind() {
    return this.modelData.provider_kind;
  }

  get isConnected() {
    return this.modelData.is_connected;
  }
}
